import React from 'react'
import './ICSDateRange.css'
import { Control, useController } from 'react-hook-form'
import DateBox from 'devextreme-react/date-box'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone';
import { useAppSelector } from '../../hooks/useAppSelector'



interface ICSDateRangeProps {
  id?: string
  name: string
  control: Control<any>
  label?: string
  inputAttr?: unknown
}

dayjs.extend(utc); // Extend dayjs to support timezone functionality
dayjs.extend(timezone); // Extend dayjs to support timezone functionality


const ICSDateRange: React.FC<ICSDateRangeProps>  = ({
  name,
  id = `${name}DateBox`,
  control,
  label = 'Date Range',
  inputAttr,
}) => {
  const {
      field: { value: startValue, onChange: startOnChange, ref: startRef },
      fieldState: { invalid: startInvalid, error: startError, isTouched: startDateTouched, isDirty: startDateDirty }
    } = useController({
      name: `${name}.start`,
      control
    })

    

  const {
    field: { value: endValue, onChange: endOnChange, ref: endRef },
    fieldState: { invalid: endInvalid, error: endError, isTouched: endDateTouched, isDirty: endDateDirty }
  } = useController({
    name: `${name}.end`,
    control
  })

  const t = useAppSelector((state) => state.userPreference.data.timezone)
  const userTime = t?.val;

  function subtractOneMinute(date:Date):Date {
    const copy = new Date(date)
    copy.setMinutes(copy.getMinutes() -1 )
    return copy
  }

  const now = new Date(); 
  
  const offesetMinutes = dayjs().tz(userTime?.slice(6)).utcOffset(); // e.g., 480 minutes (UTC+8)
  
  const nowTimezone = new Date(now.getTime() + offesetMinutes*60000 + now.getTimezoneOffset()*60000)
  new Date().getTimezoneOffset()

  function changeOutput(date:Date) {
    return new Date(date.getTime() - (offesetMinutes + date.getTimezoneOffset())*60000)
  }

  function changeInput(date:Date | null) {
    if(date != null) {
      return new Date(date.getTime() + (offesetMinutes + date.getTimezoneOffset())*60000)
    }
  }


  return (
            <>
              <DateBox
                id={`${id}Start`}
                placeholder="Start Date"
                name={`${name}.start`}
                type='datetime'
                showClearButton={true}
                value={changeInput(startValue) || undefined}
                max={endValue ? changeInput(subtractOneMinute(endValue)) : nowTimezone ? subtractOneMinute(nowTimezone) : undefined}
                isValid={!startInvalid}
                displayFormat='yyyy/MM/dd HH:mm:ss'
                onValueChanged={e => {
                  if (e.value === null) {
                    startOnChange(e.value)
                  } else {
                    startOnChange(changeOutput(e.value))
                  }
                }}
                className={
                  !(startDateTouched || startDateDirty)
                    ? ''
                    : !startInvalid
                    ? 'custom-valid-datebox'
                    : 'custom-invalid-datebox'
                }
                ref={startRef}
                acceptCustomValue={true}
              />
            
              <DateBox
                id={`${id}End`}
                placeholder="End Date"
                name={`${name}.end`}
                type='datetime'
                showClearButton={true}
                value={changeInput(endValue) || undefined}
                min={startValue ? changeInput(startValue) : undefined}
                max={nowTimezone}
                isValid={!endInvalid}
                displayFormat='yyyy/MM/dd HH:mm:ss'
                onValueChanged={e => {
                  if (e.value === null) {
                    endOnChange(e.value)
                  } else {
                    endOnChange(changeOutput(e.value))
                  }
                }}
                className={
                  !(endDateTouched || endDateDirty)
                    ? ''
                    : !endInvalid
                    ? 'custom-valid-datebox'
                    : 'custom-invalid-datebox'
                }
                ref={endRef}
                acceptCustomValue={true}
                inputAttr={inputAttr}
              />
            </>
          )
}

export default ICSDateRange
